import {
  RepoCache,
  type GetObservableOptions,
} from "@repo/shared-client/repoCache";
import {
  CreateMeetingDtoSchema,
  PatchMeetingDtoSchema,
  type Meeting,
} from "@repo/types";
import { agendaRepo, type AgendaDto } from "./agendaRepo";
import { ObjectKeyHasher } from "@repo/lib/keyHasher";
import type { AnyClock } from "@repo/lib/clock";
import {
  cacheDefaults,
  hookPriorities,
  messageServiceSubscribe,
  SubscriptionMap,
} from "./common";
import { apiService } from "@/services/apiService";
import { of, type Observable } from "rxjs";
import { nullObservable } from "@repo/shared-client/commonObservables";
import type { OnlyRequire } from "@repo/lib/types";
import { addHours } from "date-fns";
import { letterHeadRepo, type LetterHeadDto } from "./letterHeadRepo";
import { RepoMutationHooks } from "./repoMutationHooks";
import { config } from "@/config";
import { baseRepo } from "./baseRepo";

type MeetingGetKey = { type: "get"; tenantId: string; meetingId: string };
type MeetingListKey = { type: "list"; tenantId: string };
type MeetingKey = MeetingGetKey | MeetingListKey;

export type MeetingDto = Meeting & {
  tenantId: string;
  agenda: AgendaDto | null;
  letterHead: LetterHeadDto | null;
};

export type MeetingIdPath = { tenantId: string; meetingId: string | null };

export class MeetingRepository {
  private cache: RepoCache<string, MeetingDto, ObjectKeyHasher, AnyClock>;
  private hooks = new RepoMutationHooks<MeetingIdPath, MeetingDto>();
  private subscribeMap = new SubscriptionMap();

  // expose hook registry
  addHook = this.hooks.addHook;
  removeHook = this.hooks.removeHook;

  publishNewer(meeting: MeetingDto) {
    this.cache.publish(meeting.id, meeting);
  }

  meetingToDto(tenantId: string, meeting: Meeting): MeetingDto;
  meetingToDto(tenantId: string, meeting?: null): null;
  meetingToDto(tenantId: string, meeting?: Meeting | null): MeetingDto | null;
  meetingToDto(tenantId: string, meeting?: Meeting | null): MeetingDto | null {
    const agenda = agendaRepo.agendaToDto(tenantId, meeting?.agenda);
    const letterHead = letterHeadRepo.letterHeadToDto(
      tenantId,
      meeting?.letterHead
    );
    return meeting ? { ...meeting, tenantId, agenda, letterHead } : null;
  }

  constructor() {
    this.cache = new RepoCache({
      expiresAfterSeconds: cacheDefaults.expiresAfterSeconds,
      purgeExpirationAfterSeconds: cacheDefaults.purgeExpiredAfterSeconds,
      requestKeyHasher: new ObjectKeyHasher({ sortKeys: false }),
      fetch: async ({ requestKey, publish }) => {
        const key = requestKey as MeetingKey;

        switch (key.type) {
          case "get": {
            const { tenantId, meetingId } = key;
            const meeting = (
              await apiService.v1.getMeeting(tenantId, meetingId)
            ).data;

            // only publish if it's the right tenant
            const prev = this.cache.table.get(meetingId);
            if (!prev || prev.tenantId === tenantId) {
              publish(meetingId, this.meetingToDto(tenantId, meeting));
            }
            break;
          }
          case "list": {
            const { tenantId } = key;
            const meetings = (await apiService.v1.listMeetings(tenantId)).data;
            for (const meeting of meetings) {
              publish(meeting.id, this.meetingToDto(tenantId, meeting));
            }
            break;
          }
        }
      },
      collect: ({ requestKey, table }) => {
        const key = requestKey as MeetingKey;

        switch (key.type) {
          case "get": {
            return table.get(key.meetingId) ?? null;
          }
          case "list": {
            const meetings: MeetingDto[] = [];
            for (const meeting of table.values()) {
              if (meeting.tenantId === key.tenantId) {
                meetings.push(meeting);
              }
            }
            return meetings;
          }
        }
      },
      affectedKeys: ({ entryData, affectsKey }) => {
        // extract
        const { tenantId, id } = entryData;
        // affect
        affectsKey(this.getKey(tenantId, id));
        affectsKey(this.listKey(tenantId));
      },
      initialSubscribe: ({ strKey, requestKey }) => {
        const key = requestKey as MeetingKey;
        if (key.type !== "get" && key.type !== "list") return;

        const subscription = messageServiceSubscribe(
          "changing.meetings",
          key.type === "get" ? [key.meetingId] : [],
          () => this.cache.fetch(key)
        );

        this.subscribeMap.addSubscription(strKey, subscription);
      },
      subscriptionPublished: ({ strKey, requestKey, collectedData }) => {
        const key = requestKey as MeetingKey;

        switch (key.type) {
          case "get": {
            const data = collectedData as MeetingDto | null;
            if (!data) {
              this.subscribeMap.unsubscribe(strKey);
              break;
            }

            this.subscribeMap.updateChildren({
              strKey,
              idArray: data.agendaId ? [data.agendaId] : [],
              objectArray: data.agenda ? [data.agenda] : [],
              subscribeChild: (id) =>
                agendaRepo.get(key.tenantId, id, { initialFetch: false }),
              publishParent: (idArray, objectArray) => {
                if (idArray.length > 1 || objectArray.length > 1) {
                  throw new Error(
                    "Assertion failed: Agenda update somehow caused meeting to have multiple agendas."
                  );
                }
                data.agendaId = idArray[0] ?? null;
                data.agenda = objectArray[0] ?? null;
                this.cache.publish(key.meetingId, data);
              },
            });
            break;
          }
        }
      },
      finalUnsubscribe: ({ strKey }) => {
        this.subscribeMap.unsubscribe(strKey);
      },
    });

    baseRepo.addRepo(this);
  }

  private getKey(tenantId: string, meetingId: string): MeetingGetKey {
    return { type: "get", tenantId, meetingId };
  }
  private listKey(tenantId: string): MeetingListKey {
    return { type: "list", tenantId };
  }

  get(
    tenantId: string,
    meetingId: string,
    options?: GetObservableOptions
  ): Observable<MeetingDto | null> {
    if (!tenantId || !meetingId) {
      return nullObservable;
    }
    const key = this.getKey(tenantId, meetingId);
    return this.cache.observable(key, options);
  }
  getCached(tenantId: string, meetingId: string): MeetingDto | null {
    const key = this.getKey(tenantId, meetingId);
    return this.cache.collectIfUnexpired(key, null);
  }

  list(
    tenantId: string,
    options?: GetObservableOptions
  ): Observable<MeetingDto[]> {
    if (!tenantId) {
      return of([]);
    }
    const key = this.listKey(tenantId);
    return this.cache.observable(key, options);
  }
  listCached(tenantId: string): MeetingDto[] | null {
    const key = this.listKey(tenantId);
    return this.cache.collectIfUnexpired(key, null);
  }

  async create(
    tenantId: string,
    data: Omit<Partial<MeetingDto>, "agenda" | "letterHead">
  ): Promise<MeetingDto> {
    if (!tenantId) {
      throw new Error("Failed to create meeting: tenantId is blank.");
    }

    const hooks = this.hooks.execute({ tenantId, meetingId: null }, "create");
    data = await hooks.before(data);

    let created = this.meetingToDto(
      tenantId,
      (
        await apiService.v1.createMeeting(
          tenantId,
          CreateMeetingDtoSchema.parse(data)
        )
      ).data
    );

    created = await hooks.after(created);

    // publish new meeting
    this.cache.publish(created.id, created);

    return created;
  }

  async update(
    tenantId: string,
    meetingId: string,
    data: Omit<Partial<MeetingDto>, "agenda" | "letterHead">
  ): Promise<MeetingDto> {
    if (!tenantId || !meetingId) {
      throw new Error(
        "Failed to update meeting: tenantId or meetingId is blank,"
      );
    }

    const hooks = this.hooks.execute({ tenantId, meetingId }, "update");
    data = await hooks.before(data);

    let updated = this.meetingToDto(
      tenantId,
      (
        await apiService.v1.updateMeeting(
          tenantId,
          meetingId,
          PatchMeetingDtoSchema.parse(data)
        )
      ).data
    );

    updated = await hooks.after(updated);

    // publish updated meeting
    this.cache.publish(updated.id, updated);

    return updated;
  }

  async delete(tenantId: string, meetingId: string): Promise<void> {
    if (!tenantId || !meetingId) {
      throw new Error(
        "Failed to delete meeting: tenantId or meetingId is blank,"
      );
    }

    const hooks = this.hooks.execute({ tenantId, meetingId }, "delete");
    await hooks.before(null);

    await apiService.v1.deleteMeeting(tenantId, meetingId);

    await hooks.after(null);
    this.cache.publish(meetingId, null);
  }

  getAgendaPdfUrl(tenantId: string, meetingId: string) {
    return `${config.urls.api}/v1/tenants/${tenantId}/meetings/${meetingId}/agendaPdf`;
  }

  invalidate(meeting: OnlyRequire<MeetingDto, "id">) {
    this.cache.invalidateEntry(meeting.id);
  }

  invalidateAll() {
    this.cache.clear();
  }

  init(data?: Partial<MeetingDto>): MeetingDto {
    const now = new Date();

    const fullMeeting: MeetingDto = baseRepo.init({
      tenantId: baseRepo.getTenantId() || "UNKNOWN",
      status: "draft",
      name: "",
      description: "",
      agendaId: "",
      agenda: null,
      letterHead: null,
      startTime: now,
      endTime: undefined!,
      publishedAt: null,
      ...data,
    });

    if (!fullMeeting.endTime) {
      fullMeeting.endTime = addHours(now, 1);
    }

    return fullMeeting;
  }
}

export const meetingRepo = new MeetingRepository();

meetingRepo.addHook(() => {
  const before = (meeting: Partial<MeetingDto>) => {
    return { ...meeting, agenda: undefined, letterHead: undefined };
  };

  return {
    beforeCreate: before,
    beforeUpdate: before,
  };
}, hookPriorities.removeNestedEntities);
